// Editing many people at once. Every change is optional; anything left out stays as it was.
// The result is one new Tree, so a bulk edit is a single step to undo.

import { defaultPronouns, primaryName, withEventField } from './people';
import type { Gender, Id, Person, TermStyle, Tree } from './types';

export interface BulkChanges {
  /** null = not recorded. */
  living?: boolean | null;
  gender?: Gender;
  /** Empty clears them. */
  pronouns?: string;
  terms?: TermStyle;
  /** Last name on each person's main name. Empty clears it. */
  surname?: string;
  /** Empty clears these. */
  birthplace?: string;
  residence?: string;
  occupation?: string;
  addTags?: string[];
  removeTags?: string[];
  addToGroup?: { groupId: Id; role?: string };
  removeFromGroup?: Id;
  /** Added to the end of each person's notes. */
  appendNotes?: string;
}

/** One person with the changes applied. */
export function applyBulk(p: Person, c: BulkChanges): Person {
  let out = p;
  if (c.living !== undefined) out = { ...out, living: c.living };
  if (c.gender) {
    // As when editing gender in the table: fill in pronouns only for someone without any.
    const pronouns = out.pronouns || (c.pronouns === undefined ? defaultPronouns(c.gender.identity) : undefined);
    const label = c.gender.identity === 'custom' ? c.gender.label?.trim() || undefined : undefined;
    out = { ...out, gender: { identity: c.gender.identity, ...(label ? { label } : {}) }, pronouns };
  }
  if (c.pronouns !== undefined) out = { ...out, pronouns: c.pronouns.trim() || undefined };
  if (c.terms !== undefined) out = { ...out, terms: c.terms === 'auto' ? undefined : c.terms };
  if (c.surname !== undefined) {
    const surname = c.surname.trim();
    const n = primaryName(out);
    if (n) out = { ...out, names: out.names.map((x) => (x === n ? { ...x, surname } : x)) };
    else if (surname) out = { ...out, names: [{ given: '', surname, type: 'name' }, ...out.names] };
  }
  if (c.birthplace !== undefined) out = withEventField(out, 'birth', 'place', c.birthplace.trim());
  if (c.residence !== undefined) out = withEventField(out, 'residence', 'place', c.residence.trim());
  if (c.occupation !== undefined) out = withEventField(out, 'occupation', 'label', c.occupation.trim());
  if (c.addTags?.length || c.removeTags?.length) {
    const drop = new Set((c.removeTags ?? []).map((t) => t.toLowerCase()));
    out = { ...out, tags: [...new Set([...out.tags.filter((t) => !drop.has(t.toLowerCase())), ...(c.addTags ?? []).map((t) => t.trim()).filter(Boolean)])] };
  }
  const note = c.appendNotes?.trim();
  if (note) out = { ...out, notes: out.notes.trim() ? `${out.notes.trimEnd()}\n\n${note}` : note };
  return out;
}

/** Apply the changes to everyone in `ids`. `changed` counts the people who actually ended up different. */
export function bulkEdit(t: Tree, ids: Id[], c: BulkChanges): { tree: Tree; changed: number } {
  const targets = [...new Set(ids)].filter((id) => t.people[id]);
  const people = { ...t.people };
  const changedIds = new Set<Id>();
  for (const id of targets) {
    const next = applyBulk(t.people[id], c);
    if (JSON.stringify(next) !== JSON.stringify(t.people[id])) {
      people[id] = next;
      changedIds.add(id);
    }
  }
  let groups = t.groups;
  const join = c.addToGroup && t.groups[c.addToGroup.groupId];
  if (join) {
    const role = c.addToGroup!.role?.trim() || undefined;
    const fresh = targets.filter((id) => !join.members.some((m) => m.personId === id));
    if (fresh.length) {
      groups = { ...groups, [join.id]: { ...join, members: [...join.members, ...fresh.map((personId) => ({ personId, ...(role ? { role } : {}) }))] } };
      fresh.forEach((id) => changedIds.add(id));
    }
  }
  const leave = c.removeFromGroup && groups[c.removeFromGroup];
  if (leave) {
    const set = new Set(targets);
    const left = leave.members.filter((m) => set.has(m.personId));
    if (left.length) {
      groups = { ...groups, [leave.id]: { ...leave, members: leave.members.filter((m) => !set.has(m.personId)) } };
      left.forEach((m) => changedIds.add(m.personId));
    }
  }
  if (!changedIds.size) return { tree: t, changed: 0 };
  return { tree: { ...t, people, groups, updatedAt: new Date().toISOString() }, changed: changedIds.size };
}
